<script lang="ts" setup>
import { computed, onMounted, onUnmounted, PropType, ref } from "vue";
import {
  TirPmVideoPlayerControls,
  TirPmVideoPlayerSlider,
  TirPmVideoPlayerTitle,
} from "./components";
import { VideoModel } from "./models";
import { watch } from "vue";

/**Яндекс видеоплеер */
const player = ref();

/**Параметры */
const props = defineProps({
  /**Ссылка на видеозапись */
  video: { type: Object as PropType<VideoModel> },
});
/**События */
const emit = defineEmits<{
  /**Обновление имени файла */
  updateName: [name: string];
}>();

/**Наведен ли курсор на видеоплеер */
const isOver = ref(false);
/**Таймер на сокрытие интерфейса после наведения */
const overTimeout = ref();
/**Время видео */
const duration = ref(0);
/**Текущее время проигрывания видео */
const currentTime = ref(0);
/**Проигрывается ли аудио */
const play = ref(false);
/**Всегда отображаются параметры интерфейса */
const alwaysOver = ref(false);
/**Активирован полноэкранный режим */
const fullscreen = ref(false);
/**Стиль видеоплеера */
const componentStyle = ref();
/**Элемент плеера */
const $player = ref<HTMLElement>();
/**Параметры плеера в сжатом режиме */
const smallComponentRect = ref<DOMRect>();
/**Элемент для видео */
const $video = ref<HTMLIFrameElement>();
/**Временная перемення play */
const tempPlay = ref(false);

/**Классы компонента */
const componentClasses = computed(() => ({
  fullscreen: fullscreen.value,
  hidden: !isOver.value,
}));

/**Обновление плеера при переключении на другое видео */
watch(
  () => props.video?.Link,
  () => {
    initPlayer();
  },
);

/**После рендера */
onMounted(async () => {
  document.addEventListener("keydown", keyPress);
  await checkYandexSdkScript();
});
/**После удаления компонента */
onUnmounted(() => {
  document.removeEventListener("keydown", keyPress);
  player.value?.destroy();
});

/**Проверка/Установка яндекс SDK скрипта, для видеоплеера */
const checkYandexSdkScript = () => {
  return new Promise((resolve) => {
    const id = "ya-sdk";

    const initedScript = document.head.querySelector(`script#${id}`);
    if (initedScript) {
      initPlayer();
      resolve(true);
    }

    const script = document.createElement("script");
    script.type = "text/javascript";
    script.id = id;
    script.src =
      "https://storage.yandexcloud.net/videoplatform-public/player/yandex-video-player-iframe-api-bundles/1.0-11944/js/player-sdk.js";
    script.onload = () => {
      initPlayer();
      resolve(true);
    };

    document.head.appendChild(script);
  });
};
/**Инициализация плеера */
const initPlayer = async () => {
  if (!props.video) return;
  //@ts-ignore
  player.value = Ya.playerSdk.init({
    element: $video.value,
    source: props.video?.Link,
    autoplay: props.video?.AutoPlay,
    hiddenControls: "*",
  });

  duration.value = player.value.getState()?.duration;
  while (!duration.value) {
    await timeout(100);
    duration.value = player.value.getState()?.duration;
  }
  duration.value = player.value.getState().duration;
  player.value.on("CurrentTimeChange", (e: { currentTime: number }) => {
    currentTime.value = e.currentTime;
  });
  player.value.on("StatusChange", (e: { status: string }) => {
    play.value = e.status == "play";
  });
};
/**Таймер */
const timeout = (ms: number) => {
  return new Promise((resolve) => {
    setTimeout(() => resolve(true), ms);
  });
};

/**Отслеживание нажатия клавиш */
function keyPress(e: KeyboardEvent) {
  switch (e.code) {
    case "Escape":
      if (fullscreen.value) {
        fullscreen.value = !fullscreen.value;
        toggleFullscreen();
      }
      break;
    case "Space":
      play.value = !play.value;
      togglePlay();
      break;
    case "ArrowLeft":
      setTime(Math.max(0, currentTime.value - 5));
      break;
    case "ArrowRight":
      setTime(Math.min(duration.value, currentTime.value + 5));
      break;
  }
}
/**Переключение флага отвечающего за наведение курсора */
const onMouseMove = () => {
  if (alwaysOver.value) return;

  isOver.value = true;
  clearTimeout(overTimeout.value);
  overTimeout.value = setTimeout(() => {
    isOver.value = false;
  }, 2000);
};
/**Отключение сокрытия */
const disableHide = () => {
  clearTimeout(overTimeout.value);
  alwaysOver.value = true;
  isOver.value = true;
};
/**При отпускании ЛКМ на слайдере */
const enableHide = () => {
  alwaysOver.value = false;
  onMouseMove();
};
/**Установка текущего времени проигрывания */
const setTime = (time: number) => {
  currentTime.value = time;
  player.value.seek(time);
};
/**Переключение полноэкранного режима */
const toggleFullscreen = () => {
  const rect = $player.value?.getBoundingClientRect();
  if (fullscreen.value && rect) {
    smallComponentRect.value = rect;
    componentStyle.value = getStylesByRect(rect);
    setTimeout(() => {
      componentStyle.value = {
        position: "fixed",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        transition: "all 0.3s",
      };
    });
  } else {
    componentStyle.value = smallComponentRect.value
      ? getStylesByRect(smallComponentRect.value)
      : {};
    setTimeout(() => {
      Object.assign(componentStyle.value, {
        position: "relative",
        top: 0,
        left: 0,
        transition: "unset",
      });
      setTimeout(() => {
        componentStyle.value = undefined;
      }, 300);
    }, 300);
  }
};
/**Получение стилей по параметрам */
const getStylesByRect = (rect: DOMRect) => {
  return {
    position: "fixed",
    width: rect.width / 16 + "rem",
    height: rect.height / 16 + "rem",
    top: rect.top / 16 + "rem",
    left: rect.left / 16 + "rem",
    transition: "all 0.3s",
    zIndex: 9999,
  };
};
/**Переключение видимости поповера */
const togglePopover = (isOpen: boolean) => {
  if (isOpen) {
    disableHide();
  } else {
    enableHide();
  }
};
/**Изменение имени видедозаписи */
const updateName = (name: string) => {
  if (props.video) {
    props.video.Name = name;
    emit("updateName", name);
  }
  onMouseMove();
};
/**Переключение паузы/проигрывания */
const togglePlay = () => {
  if (play.value) {
    player.value.play();
  } else {
    player.value.pause();
  }
};
/**Использование слайдера */
const sliderMouseDown = () => {
  disableHide();
  player.value.pause();
  play.value = false;
  tempPlay.value = false;
};
/**Окончание использования слайдера */
const sliderMouseUp = () => {
  enableHide();

  const state = player.value?.getState();
  if (state.currentTime < state.duration && !tempPlay.value) {
    player.value?.play();
    play.value = true;
  } else {
    player.value?.pause();
    play.value = false;
  }
  tempPlay.value = play.value;
};
/**Переключение состояния при клике по видео */
const onVideoClick = () => {
  play.value = !play.value;
  togglePlay();
};
/**Переключение полноэкранного режима при двойном клике по видео */
const onVideoDblClick = () => {
  fullscreen.value = !fullscreen.value;
  toggleFullscreen();
};
</script>
<template>
  <div
    class="tir-pm-video-player"
    ref="$player"
    :class="componentClasses"
    :style="componentStyle"
    @mousemove="onMouseMove"
  >
    <Transition name="tir-pm-video-player_animation">
      <TirPmVideoPlayerTitle v-if="isOver" :name="video?.Name" />
    </Transition>
    <div
      class="tir-pm-video-player_wrapper"
      ref="$video"
      @click="onVideoClick"
      @dblclick="onVideoDblClick"
    />
    <div class="tir-pm-video-player_footer">
      <TirPmVideoPlayerSlider
        v-model:play="play"
        :duration="duration"
        :currentTime="currentTime"
        :isOver="isOver"
        @mouse-down="sliderMouseDown"
        @mouse-up="sliderMouseUp"
        @switch="setTime"
      />
      <Transition name="tir-pm-video-player_animation">
        <TirPmVideoPlayerControls
          v-if="isOver"
          v-model:play="play"
          v-model:fullscreen="fullscreen"
          :duration="duration"
          :currentTime="currentTime"
          :name="video?.Name"
          @update:fullscreen="toggleFullscreen"
          @update:play="togglePlay"
          @popover="togglePopover"
          @updateName="updateName"
        />
      </Transition>
    </div>
  </div>
</template>
<style lang="scss" scoped>
.tir-pm-video-player {
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  border-radius: 0.5rem;
  background-color: #000;
  &.fullscreen {
    z-index: 9999;
    border-radius: 0;
  }
  &.hidden {
    overflow: hidden;
  }
  &_wrapper {
    width: 100%;
    aspect-ratio: 16/9;
    border-radius: 0.5rem;
    overflow: hidden;
  }
  &_animation-enter-active,
  &_animation-leave-active {
    height: 2rem;
    transition: 0.3s;
  }
  &_animation-enter-from,
  &_animation-leave-to {
    height: 0;
    transition: 0.3s;
  }
  &_footer {
    position: absolute;
    bottom: 0;
    left: 0;
    width: 100%;
  }
}
</style>
<style lang="scss">
.tir-pm-video-player_wrapper yaplayerskintag {
  display: none !important;
}
</style>
